import { Select, TextInput } from "../forms";
import i18n from "@breward/i18n";
import { useSearchParams } from "react-router-dom";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import Drawer from "../Drawer/Drawer";
import { Button } from "../Button/Button";
import { useDebounceFn } from "ahooks";

const FiltersBar = ({ config }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const hasFilters = useMemo(() => {
    return Array.isArray(config?.filters) && config.filters.length > 0;
  }, [config?.filters]);

  useEffect(() => {
    const parsedQuery = Object.fromEntries(searchParams);
    const parsedFilter = qs.parse(parsedQuery?.filter) || {};
    const parsedSearch = typeof parsedQuery?.q === "string" ? parsedQuery.q : "";

    setSearchValue(parsedSearch);
    setFilterValues(parsedFilter as Record<string, any>);

    if (config?.searchAvailable) {
      config?.onSearch(parsedSearch);
    }

    if (hasFilters) {
      config?.onFilter(parsedFilter);
    }
  }, [searchParams]);

  const debounceSearch = useDebounceFn(
    (value: string) => {
      const parsedQuery = Object.fromEntries(searchParams);
      const nextParams: Record<string, string> = {};

      if (value?.length > 0) {
        nextParams.q = value;
      }
      if (parsedQuery?.filter) {
        nextParams.filter = parsedQuery.filter as string;
      }

      setSearchParams(nextParams);
    },
    { wait: 600 }
  );

  const getCleanFilters = () => {
    if (!filterValues) return {};
    return Object.fromEntries(
      Object.entries(filterValues).filter(
        ([, value]) => value !== undefined && value !== null && value !== ""
      )
    );
  };

  const handleApply = () => {
    const cleanedFilters = getCleanFilters();
    const nextParams: Record<string, string> = {};

    if (config?.searchAvailable && searchValue?.length > 0) {
      nextParams.q = searchValue;
    }

    if (Object.keys(cleanedFilters).length > 0) {
      nextParams.filter = qs.stringify(cleanedFilters);
    }

    setSearchParams(nextParams);

    if (!nextParams.q && !nextParams.filter) {
      config?.onClear();
    }

    setDrawerOpened(false);
  };

  const handleClear = () => {
    setSearchValue("");
    setFilterValues({});
    setSearchParams({});
    config?.onClear();
  };

  return (
    <div className="flex flex-1 flex-col gap-3">
      <div className="flex flex-row items-end justify-between gap-3">
        {config?.searchAvailable && (
          <div className="flex-1">
            <TextInput
              onChange={(e) => {
                const nextValue = e.target.value;
                setSearchValue(nextValue);
                debounceSearch.run(nextValue);
              }}
              label={i18n.t("global:shared.searchInput.label", "Search")}
              endAddon={
                <div className="icon-[ph--magnifying-glass] text-content-light h-4 w-4" />
              }
              value={searchValue}
            />
          </div>
        )}

        <div className="flex flex-row items-center gap-3">
          <Button
            type="secondary"
            variant="outlined"
            onClick={() => setDrawerOpened(true)}
            startIcon={<div className="icon-[ph--funnel] h-4 w-4" />}
          >
            {i18n.t("global:shared.filtersButton", "Filtruj")}
          </Button>
        </div>
      </div>

      <Drawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        title={i18n.t("global:shared.filtersTitle", "Filtry")}
        width={480}
        footer={
          <div className="flex items-center justify-between gap-3">
            <Button
              type="secondary"
              variant="outlined"
              onClick={handleClear}
              className="flex items-center gap-2"
            >
              <div className="icon-[ph--broom] h-4 w-4" />
              {i18n.t("global:shared.clearFilters", "Clear filters")}
            </Button>
            <Button type="primary" onClick={handleApply}>
              {i18n.t("global:shared.applyFilters", "Zatwierdź")}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          {config?.filters?.map((filter) => (
            <Select
              key={filter?.field}
              onChange={(val) => {
                setFilterValues((prev) => ({
                  ...(prev || {}),
                  [`${filter?.field}`]: val,
                }));
              }}
              label={filter?.label}
              options={filter?.options}
              selected={filterValues?.[filter?.field]}
            />
          ))}

          {!config?.searchAvailable && !hasFilters && (
            <div className="text-sm text-primary-500 dark:text-primary-400">
              {i18n.t("global:shared.noFilters", "No filters available")}
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default FiltersBar;
